import { useUpdateCandidate } from '@/hooks/use-candidates';
import { usePermission } from '@/lib/permissions';
import { isConsentLocked } from '../lib/consent-actions';
import type {
  CandidateDetail,
  RelocationStatus,
  UpdateCandidateInput,
} from '@/types/candidates';
import { CANDIDATE_SOURCE_OPTIONS } from '../lib/candidate-source';
import { InlineNumberField } from './inline-number-field';
import { InlineSelectField } from './inline-select-field';
import { InlineTextField } from './inline-text-field';

const CURRENCY_OPTIONS: Array<{ value: 'USD' | 'EUR' | 'UAH'; label: string }> = [
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'UAH', label: 'UAH' },
];

const RELOCATION_OPTIONS: Array<{ value: RelocationStatus; label: string }> = [
  { value: 'OPEN', label: 'Open' },
  { value: 'CONDITIONAL', label: 'Conditional' },
  { value: 'NOT_OPEN', label: 'Not open' },
];

interface CandidateContactCardProps {
  candidate: CandidateDetail;
}

export function CandidateContactCard({ candidate }: CandidateContactCardProps) {
  // HW-1055: a candidate who revoked consent is read-only until anonymised.
  const canEdit = usePermission('recruitment.candidates.edit') && !isConsentLocked(candidate);
  const { mutateAsync } = useUpdateCandidate(candidate.id);

  const save = async (patch: UpdateCandidateInput) => {
    await mutateAsync(patch);
  };

  return (
    <section className="rounded-lg border border-border bg-background p-5">
      <h2 className="mb-4 text-base font-medium">Contact & Identity</h2>
      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        <Field label="Full name">
          <InlineTextField
            value={candidate.fullName}
            onSave={(next) => save({ fullName: next ?? '' })}
            disabled={!canEdit}
          />
        </Field>
        <Field label="Headline">
          <InlineTextField
            value={candidate.headline}
            onSave={(next) => save({ headline: next })}
            multiline
            disabled={!canEdit}
          />
        </Field>
        <Field label="Email">
          <InlineTextField
            value={candidate.email}
            onSave={(next) => save({ email: next })}
            type="email"
            disabled={!canEdit}
          />
        </Field>
        <Field label="Phone">
          <InlineTextField
            value={candidate.phone}
            onSave={(next) => save({ phone: next })}
            type="tel"
            disabled={!canEdit}
          />
        </Field>
        <Field label="LinkedIn URL">
          <InlineTextField
            value={candidate.linkedinUrl}
            onSave={(next) => save({ linkedinUrl: next })}
            type="url"
            placeholder="https://linkedin.com/in/…"
            disabled={!canEdit}
          />
        </Field>
        <Field label="Telegram">
          <InlineTextField
            value={candidate.telegram}
            onSave={(next) => save({ telegram: next })}
            placeholder="@handle or t.me/…"
            disabled={!canEdit}
          />
        </Field>
        <Field label="Location">
          <InlineTextField
            value={candidate.location}
            onSave={(next) => save({ location: next })}
            disabled={!canEdit}
          />
        </Field>
        <Field label="Relocation">
          <InlineSelectField
            value={candidate.relocation}
            options={RELOCATION_OPTIONS}
            onSave={(next) => save({ relocation: next })}
            disabled={!canEdit}
          />
        </Field>
        <Field label="Source">
          <InlineSelectField
            value={candidate.source}
            options={CANDIDATE_SOURCE_OPTIONS}
            onSave={(next) => save({ source: next })}
            disabled={!canEdit}
          />
        </Field>
        <Field label="Source details">
          <InlineTextField
            value={candidate.sourceDetails}
            onSave={(next) => save({ sourceDetails: next })}
            disabled={!canEdit}
          />
        </Field>
        <Field label="Expected salary">
          <InlineNumberField
            value={candidate.expectedSalary}
            onSave={(next) => save({ expectedSalary: next })}
            max={10_000_000}
            renderDisplay={(v) => (v !== null ? v.toLocaleString() : '—')}
            disabled={!canEdit}
          />
        </Field>
        <Field label="Currency">
          <InlineSelectField
            value={candidate.salaryCurrency}
            options={CURRENCY_OPTIONS}
            onSave={(next) => save({ salaryCurrency: next })}
            disabled={!canEdit}
          />
        </Field>
      </dl>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd>{children}</dd>
    </div>
  );
}
